import {
  DEFAULT_EXERCISE_MINUTES,
  type ExerciseKind,
  initialsOf,
  memberWeekView,
} from '@echo/core';
import {
  todayIso,
  useGroupPeople,
  useMarkExercise,
  useMe,
  useMyGroup,
  useMyProgress,
  useMyStats,
  useSubGroupProgress,
  useToday,
} from '@echo/core/react';
import { Avatar, Button, Chip, ListRow } from '@echo/ui-web';
import { plural } from '@lingui/core/macro';
import { useLingui as useLinguiRuntime } from '@lingui/react';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, Trash2, UserCog } from 'lucide-react';
import { useState } from 'react';
import { DailyLogCard } from '@/components/daily-log-card';
import { ToolbarBack } from '@/components/empty-state-frame';
import { ExerciseRow } from '@/components/exercise-row';
import { GroupActionFailed, GroupLoadFailed, GroupLoading } from '@/components/group-load-state';
import { GroupRoleTag } from '@/components/group-role-tag';
import { MarkTodoCard, TodoListHeading } from '@/components/mark-todo-card';
import { OverviewCard } from '@/components/overview-card';
import { CompletionCard, WeekChartCard } from '@/components/progress-cards';
import { useAuthorizedImageUrl } from '@/lib/authorized-image';
import { exerciseOpen } from '@/lib/exercise-routes';
import { useGoBack } from '@/lib/use-go-back';

export const Route = createFileRoute('/_app/account_/groups_/$id_/member/$personId')({
  component: MemberProfilePage,
});

const AVATAR = 143;

/**
 * People and access › a person (Figma account/groups, "Member profile"): who they are, since
 * when, their role, and Manage member — Manage access and Remove from group (your own profile:
 * a Manage access chip). Then their
 * progress: your own from your sessions, with today's to-do as on Home; someone else's from echo's backend, which reads it as
 * the Stronger Memory group — today's to-do with "Mark done", the log, Overview, this week and
 * completion. Never their answers or how long they took. A Facilitator has no progress: they
 * don't exercise.
 */
function MemberProfilePage() {
  const { t } = useLingui();
  const { i18n } = useLinguiRuntime();
  const navigate = useNavigate();
  const me = useMe();
  const { id, personId } = Route.useParams();
  const people = useGroupPeople(id);
  const person = people.data?.find((candidate) => candidate.id === personId);
  const photo = useAuthorizedImageUrl(me.data?.profileImage?.medium?.url ?? undefined);
  // The day picked in the log's date selector; the log ends on today until one is.
  const [logDay, setLogDay] = useState<string>();
  const progress = useMyProgress(logDay);
  const stats = useMyStats();
  const today = useToday();
  const { group } = useMyGroup(id);
  const exercises = person !== undefined && person.role !== 'facilitator';
  const others = useSubGroupProgress(id, exercises && !person.you);
  const marking = useMarkExercise(id);
  const goBack = useGoBack(
    () => void navigate({ to: '/account/groups/$id/people', params: { id }, replace: true }),
  );
  const toolbar = (
    <ToolbarBack
      render={({ className, children }) => (
        <button type="button" className={className} onClick={goBack}>
          {children}
        </button>
      )}
    />
  );
  if (people.isPending) {
    return (
      <>
        {toolbar}
        <GroupLoading />
      </>
    );
  }
  if (people.isError) {
    return (
      <>
        {toolbar}
        <div className="pt-content-md">
          <GroupLoadFailed onRetry={() => void people.refetch()} />
        </div>
      </>
    );
  }
  // Removed (on its way out) or an unknown id from a link.
  if (!person) return toolbar;
  // A member reaches the profiles from Members too, to see how others are doing; only a
  // leader gets the controls.
  const manages = group?.manages ?? false;

  const name = person.you ? (me.data?.name ?? me.data?.firstName ?? '') : person.name;
  const joined = i18n.date(new Date(person.joinedAt), { month: 'long', year: 'numeric' });
  const chevron = <ChevronRight size={24} className="text-icon-medium" aria-hidden />;
  const params = { id, personId: person.id };
  const firstName = person.you ? (me.data?.firstName ?? name) : name;
  const exercisesThisWeek = progress.data?.exercisesThisWeek ?? 0;
  // Today's rows know what is under way, so the log opens an exercise the way Home does.
  const statusOf = (kind: ExerciseKind) =>
    today.data?.exercises.find((exercise) => exercise.kind === kind)?.status ?? 'todo';

  return (
    <>
      {toolbar}
      <div className="flex flex-col gap-section-sm pt-content-md">
        <div className="flex flex-col items-center gap-internal-lg">
          {/* Figma has no page title: the name is the heading, and the photo or initials would
              only repeat it. */}
          <span aria-hidden className="inline-flex">
            <Avatar
              size={AVATAR}
              src={person.you ? photo : undefined}
              initials={initialsOf(name)}
              tone="olive"
              label={name}
            />
          </span>
          <div className="flex w-full flex-col items-center">
            <h1 className="text-center text-headline-lg font-normal">{name}</h1>
            <div className="flex flex-wrap items-center justify-center gap-x-content-sm gap-y-internal-sm">
              <span className="text-body-sm text-on-surface-medium">
                <Trans>Joined {joined}</Trans>
              </span>
              <GroupRoleTag role={person.role} />
            </div>
          </div>
        </div>

        {!manages ? null : person.you ? (
          // Figma progress/member (your own): one Manage access chip; leaving is Manage group's.
          <div className="flex justify-center">
            <Chip
              variant="assist"
              render={<Link to="/account/groups/$id/member/$personId/access" params={params} />}
            >
              <UserCog size={20} aria-hidden />
              <Trans>Manage access</Trans>
            </Chip>
          </div>
        ) : (
          <section className="flex flex-col gap-content-sm rounded-xl bg-surface px-content-sm py-content-md">
            <h2 className="text-title-lg font-bold">
              <Trans>Manage member</Trans>
            </h2>
            <div className="flex flex-col">
              <ListRow
                icon={<UserCog size={24} className="shrink-0 text-icon-strong" aria-hidden />}
                label={t`Manage access`}
                trailing={chevron}
                onClick={() =>
                  void navigate({ to: '/account/groups/$id/member/$personId/access', params })
                }
              />
              <ListRow
                icon={<Trash2 size={24} className="shrink-0 text-action-destructive" aria-hidden />}
                label={t`Remove from group`}
                destructive
                trailing={chevron}
                className="border-b-0"
                onClick={() =>
                  void navigate({ to: '/account/groups/$id/member/$personId/remove', params })
                }
              />
            </div>
          </section>
        )}

        {exercises && !person.you ? (
          <MemberProgress
            firstName={firstName}
            memberActorUuid={person.id}
            progress={others}
            leads={manages}
            marking={marking}
          />
        ) : null}

        {exercises && person.you ? (
          <>
            {today.data ? (
              <section className="flex flex-col gap-content-sm" aria-labelledby="own-todo-title">
                <TodoListHeading
                  id="own-todo-title"
                  minutes={today.data.exercises[0]?.minutes ?? DEFAULT_EXERCISE_MINUTES}
                />
                <ul className="flex flex-col gap-content-sm">
                  {today.data.exercises.map((exercise) => (
                    <li key={exercise.kind}>
                      <ExerciseRow exercise={exercise} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            <DailyLogCard
              log={progress.data?.log ?? []}
              nextKind={undefined}
              startNext={() => null}
              openExercise={(kind, { className, children, ...props }) => (
                <Link
                  key={kind}
                  {...exerciseOpen(kind, statusOf(kind))}
                  className={className}
                  {...props}
                >
                  {children}
                </Link>
              )}
              dates={
                progress.data
                  ? {
                      value: logDay ?? todayIso(),
                      first: progress.data.firstDate,
                      last: todayIso(),
                      onChange: setLogDay,
                    }
                  : undefined
              }
            />
            <OverviewCard stats={stats.data} />
            <WeekChartCard
              title={t`This week`}
              subtitle={plural(exercisesThisWeek, {
                one: 'You completed # exercise this week.',
                other: 'You completed # exercises this week.',
              })}
              week={progress.data?.week ?? []}
            />
            <CompletionCard
              subtitle={t`Average completion rate this week.`}
              rates={progress.data?.completionByKind}
            />
          </>
        ) : null}
      </div>
      <div className="flex justify-end pt-content-md">
        <Button onClick={goBack}>{t`Back`}</Button>
      </div>
    </>
  );
}

/**
 * Someone else's week, from echo's backend: the log, this week and completion for everyone in
 * the group, and today's to-do with "Mark done" for a leader. The numbers come from the member's
 * newest summary, so they read 0 until the member's app has written one.
 */
function MemberProgress({
  firstName,
  memberActorUuid,
  progress,
  leads,
  marking,
}: {
  firstName: string;
  memberActorUuid: string;
  progress: ReturnType<typeof useSubGroupProgress>;
  leads: boolean;
  marking: ReturnType<typeof useMarkExercise>;
}) {
  const { t } = useLingui();
  const [logDay, setLogDay] = useState<string>();
  if (progress.isPending) return <GroupLoading />;
  if (progress.isError) {
    return (
      <div className="flex flex-col items-start gap-content-sm">
        <p className="text-body-md text-on-surface-medium">
          <Trans>{firstName}'s progress couldn't be loaded.</Trans>
        </p>
        <Button variant="secondary" onClick={() => void progress.refetch()}>
          {t`Try again`}
        </Button>
      </div>
    );
  }
  const member = progress.data.members.find((entry) => entry.actorUuid === memberActorUuid);
  if (!member) return null;
  const view = memberWeekView(member, progress.data.today, logDay);
  const summary = member.summary;
  const pending = marking.isPending ? marking.variables?.kind : undefined;
  const mark = (kind: ExerciseKind, done: boolean) =>
    marking.mutate({ memberActorUuid, kind, done });
  return (
    <>
      {leads && view ? (
        <div className="flex flex-col gap-internal-md">
          <MarkTodoCard
            today={view.today}
            undoable={view.undoable}
            pending={pending}
            onMark={(kind) => mark(kind, true)}
            onUndo={(kind) => mark(kind, false)}
          />
          {marking.isError ? <GroupActionFailed /> : null}
        </div>
      ) : null}
      {view && view.log.length > 0 ? (
        <DailyLogCard
          log={view.log}
          nextKind={undefined}
          startNext={() => null}
          dates={{
            value: logDay ?? progress.data.today,
            first: view.firstDate,
            last: progress.data.today,
            onChange: setLogDay,
          }}
        />
      ) : null}
      <div className="flex flex-col gap-internal-md">
        <OverviewCard
          stats={{
            currentStreak: summary?.currentStreak ?? 0,
            sessionsThisMonth: summary?.sessionsThisMonth ?? 0,
            bestStreak: summary?.bestStreak ?? 0,
            sessionsTotal: summary?.sessionsTotal ?? 0,
          }}
        />
        {member.reported ? null : (
          <p className="text-body-sm text-on-surface-medium">
            <Trans>{firstName} hasn't shared any progress yet.</Trans>
          </p>
        )}
      </div>
      {view ? (
        <>
          <WeekChartCard title={t`This week`} week={view.week} />
          <CompletionCard
            subtitle={t`Average completion rate this week.`}
            rates={view.completionByKind}
          />
        </>
      ) : null}
    </>
  );
}
